/* Public "Data & Account Deletion" landing page — required by Google Play
   Data Safety declaration. Must be reachable WITHOUT authentication so the
   Play Console reviewer can verify the URL returns 200.

   The page explains:
   1. How a logged-in user can self-delete from within the app.
   2. How anyone (also non-logged-in / people who lost access) can request
      deletion by emailing privacy@orio.app.
   3. What we delete immediately vs what we retain for legal reasons.
*/
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, Shield, Trash2, Clock, CheckCircle2 } from "lucide-react";

export default function DataDeletion() {
  return (
    <div className="min-h-screen bg-[#FBF7F1] text-[#1A1A1A]">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-[13px] text-[#7A7A7A] hover:text-[#1A1A1A] mb-6"
          data-testid="data-deletion-back"
        >
          <ArrowLeft className="h-4 w-4" /> Torna alla home
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-[#F0EBE3] text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C25612] mb-4">
            <Shield className="h-3 w-3" /> Privacy · GDPR
          </div>
          <h1 className="font-display text-[36px] sm:text-[44px] leading-[1.05] tracking-tight">
            Cancellazione account e dati
          </h1>
          <p className="mt-3 text-[15px] text-[#5A5A5A] max-w-2xl leading-relaxed">
            Puoi richiedere la cancellazione del tuo account OrioDog e dei tuoi
            dati in qualsiasi momento. Rispettiamo il tuo diritto all&apos;oblio
            secondo il GDPR e le linee guida Google Play.
          </p>
        </motion.header>

        {/* Method 1 — from inside the app */}
        <Section
          icon={Trash2}
          title="Opzione 1 — Dall&apos;app (utenti registrati)"
          testid="deletion-method-app"
        >
          <ol className="list-decimal pl-5 space-y-1.5 mt-2 text-[14px] text-[#3A3A3A]">
            <li>Apri l&apos;app OrioDog o accedi a <a href="https://oriodog.com" className="underline">oriodog.com</a></li>
            <li>Effettua il login con le tue credenziali</li>
            <li>Vai su <strong>Profilo → Impostazioni → Account</strong></li>
            <li>Tocca <strong>&quot;Cancella account&quot;</strong> in fondo alla pagina</li>
            <li>Conferma la richiesta digitando la tua email</li>
            <li>La cancellazione è <strong>immediata</strong></li>
          </ol>
        </Section>

        {/* Method 2 — external email request */}
        <Section
          icon={Mail}
          title="Opzione 2 — Richiesta via email (per chi ha perso l&apos;accesso)"
          testid="deletion-method-email"
        >
          <p className="text-[14px] text-[#3A3A3A] leading-relaxed">
            Se non riesci ad accedere al tuo account, scrivi a{" "}
            <a
              href="mailto:privacy@orio.app?subject=Richiesta%20cancellazione%20account%20OrioDog&body=Ciao,%0A%0AChiedo%20la%20cancellazione%20del%20mio%20account%20OrioDog%20e%20di%20tutti%20i%20dati%20associati.%0A%0AEmail%20account:%20%5Btua%20email%5D%0ANome%20cane%20(se%20ricordi):%20%5Bnome%5D%0ACitt%C3%A0:%20%5Bcitt%C3%A0%5D%0A%0AGrazie."
              className="font-semibold underline decoration-dotted"
              data-testid="deletion-email-link"
            >
              privacy@orio.app
            </a>{" "}
            includendo:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2 text-[14px] text-[#3A3A3A]">
            <li>L&apos;email con cui hai creato l&apos;account</li>
            <li>Nome del tuo cane (se lo ricordi — per identificarti)</li>
            <li>Città di residenza</li>
          </ul>
          <p className="text-[13px] text-[#7A7A7A] mt-3">
            Verificata la tua identità (24-48h), processiamo la cancellazione
            entro <strong>7 giorni</strong> e ti mandiamo una conferma via email.
          </p>
        </Section>

        {/* What we delete */}
        <Section
          icon={CheckCircle2}
          title="Cosa cancelliamo immediatamente"
          testid="deletion-what-deleted"
        >
          <ul className="list-disc pl-5 space-y-1 mt-2 text-[14px] text-[#3A3A3A]">
            <li>Il tuo account (email, nome, telefono)</li>
            <li>Le tue foto e video</li>
            <li>Le tue storie (Stories)</li>
            <li>I profili dei tuoi cani</li>
            <li>Documenti sanitari caricati nel Cassetto</li>
            <li>Messaggi in chat privata</li>
            <li>Preferenze, match, karma, cronologia passeggiate</li>
            <li>Sottoscrizioni ai push (rimozione automatica)</li>
          </ul>
        </Section>

        {/* What we retain */}
        <Section
          icon={Clock}
          title="Cosa conserviamo per legge"
          testid="deletion-what-retained"
        >
          <ul className="list-disc pl-5 space-y-1 mt-2 text-[14px] text-[#3A3A3A]">
            <li>
              <strong>Fatture Stripe</strong>: se hai pagato un abbonamento,
              per obbligo fiscale italiano manteniamo i dati fiscali per{" "}
              <strong>10 anni</strong> come previsto dal DPR 633/1972. Dopo
              vengono eliminati automaticamente.
            </li>
            <li>
              <strong>Contenuti pubblici anonimizzati</strong>: eventuali
              missioni completate o storie di community restano visibili in
              forma anonima (senza il tuo nome) se necessarie al servizio.
            </li>
            <li>
              <strong>Log di sicurezza</strong>: conservati 30 giorni per
              prevenire frodi (come richiesto dal Garante Privacy).
            </li>
          </ul>
        </Section>

        {/* Contact */}
        <div className="mt-10 p-5 rounded-2xl bg-white border border-[#F0EBE3]">
          <div className="text-[11px] uppercase tracking-widest text-[#9A9A9A] mb-1">Contatti</div>
          <div className="text-[14px] text-[#1A1A1A] leading-relaxed">
            <strong>Data Protection Officer</strong>
            <br />
            OrioDog · Italia
            <br />
            Email: <a href="mailto:privacy@orio.app" className="underline">privacy@orio.app</a>
            <br />
            Autorità di controllo: <a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="underline">Garante Privacy — garanteprivacy.it</a>
          </div>
        </div>

        <div className="mt-8 text-[12px] text-[#9A9A9A] text-center">
          <Link to="/privacy" className="underline decoration-dotted mr-4">Privacy Policy</Link>
          <Link to="/terms" className="underline decoration-dotted">Termini di servizio</Link>
          <div className="mt-2">Ultimo aggiornamento: febbraio 2026</div>
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section
      className="mt-8 p-6 rounded-2xl bg-white border border-[#F0EBE3]"
      data-testid={testid}
    >
      <div className="flex items-center gap-2.5 mb-2">
        <div
          className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
          style={{
            background: "linear-gradient(135deg, #FF7A1A22 0%, #F4B83C22 100%)",
          }}
        >
          <Icon className="h-4.5 w-4.5 text-[#C25612]" />
        </div>
        <h2 className="font-display text-[19px] leading-tight" dangerouslySetInnerHTML={{ __html: title.replace(/&apos;/g, "'") }} />
      </div>
      <div className="pl-[46px] sm:pl-0">{children}</div>
    </section>
  );
}
